import apiClient from "./apiClient";

const fetchCbdContentByType = async (type) => {
  const response = await apiClient.get(`/cpd/idl/nodes?type=${type}`);
  return response.data._embedded.nodes || [];
};

const fetchCpdContentNodesByQueryParams = async (params) => {
  const response = await apiClient.get(
    // https://nodejs.org/api/url.html#class-urlsearchparams
    `/cpd/idl/nodes?${new URLSearchParams(params)}`
  );
  return response.data._embedded?.nodes || [];
};

const fetchCbdContentNodeById = async (nodeId) => {
  const response = await apiClient.get(`/cpd/idl/nodes/${nodeId}`);
  return response.data?._embedded ?? response.data;
};

const fetchCbdContentSubNodesForNodeById = async (nodeId) => {
  const response = await apiClient.get(`/cpd/idl/nodes/${nodeId}/subnodes`);
  return response.data._embedded.nodes;
};

const fetchCbdContentTablesForNodeById = async (nodeId) => {
  const response = await apiClient.get(`/cpd/idl/nodes/${nodeId}/tables`);
  return response.data._embedded.tables || [];
};

const sendCbdContentTableForNode = async ({
  nodeUid,
  tableId,
  parentUid,
  ...rest
}) => {
  const response = await apiClient.put(`/cpd/idl/nodes/${parentUid}`, {
    table: { ...rest },
    // type: NodeType.TABLE,
  });
  return response.data;
};

const fetchCbdContentTableRowsByNodeIdAndTableId = async (nodeId, tableId) => {
  const response = await apiClient.get(
    `/cpd/idl/nodes/${nodeId}/tables/${tableId}/rows`
  );
  return response.data;
};

const fetchCbdQuizContentByNodeId = async (nodeId) => {
  const response = await apiClient.get(`/cpd/idl/nodes/${nodeId}/quiz`);
  return response.data;
};

const fetchOnlyCpdContentByQueryParams = async (params) => {
  const response = await apiClient.get(
    // https://nodejs.org/api/url.html#class-urlsearchparams
    `/conditions?${new URLSearchParams(params)}`
  );
  return response.data._embedded?.conditions || [];
};

const fetchCbdCases = async (params) => {
  const response = await apiClient.get("/cpd/cases?includeAllProps=true", {
    params: { ...params },
  });
  return response.data._embedded?.cases || [];
};

const fetchCbdCasesById = async (caseId) => {
  const response = await apiClient.get(`/cpd/cases/${caseId}`);
  return response.data;
};

const fetchCbdQuizForCaseById = async (caseId) => {
  const response = await apiClient.get(`/cpd/cases/${caseId}/quiz`);
  return response.data;
};

const createOrUpdateCbdQuizForById = async ({ nodeId, data }) => {
  const response = await apiClient.put(`/cpd/nodes/${nodeId}`, {
    ...data,
  });
  return response.data;
};

const createOrUpdateCase = async ({ body }) => {
  if (body?.uid) {
    return await apiClient.put(`/cpd/cases/update/${body?.uid}`, {
      themeUid: body?.themeUid,
      scenario: body?.scenario,
      title: body?.title,
      status: body?.status,
      questions: body?.questions || [],
      keyLearningPoints: body?.keyLearningPoints || [],
    });
  } else {
    return await apiClient.post("/cpd/cases", body);
  }
};

// CPD Analytics API Services
const fetchCpdAnalyticsSummary = async (params) => {
  const response = await apiClient.get("/analytics/cpd/summary", {
    params: { ...params },
  });
  return response.data;
};

const fetchCpdAnalyticsByDimension = async (dimension, params) => {
  const response = await apiClient.get("/analytics/cpd/by-dimension", {
    params: { dimension, ...params },
  });
  return response.data;
};

const fetchCpdUserProgress = async (params) => {
  const response = await apiClient.get("/analytics/cpd/user-progress", {
    params: { ...params },
  });
  return response.data;
};

const fetchCpdTopPerformers = async (params = {}) => {
  const { limit = 10, startDate, endDate } = params;
  const queryParams = { limit };
  if (startDate) queryParams.startDate = startDate;
  if (endDate) queryParams.endDate = endDate;
  
  const response = await apiClient.get("/analytics/cpd/top-performers", {
    params: queryParams,
  });
  return response.data;
};

const createOrUpdateCaseImage = async ({ image, caseId }) => {
  const formData = new FormData();
  formData.append("image", image?.originFileObj);

  apiClient.interceptors.request.use((config) => {
    config.headers["Content-Type"] = "multipart/form-data";
    return config;
  });

  const response = await apiClient.put(`/cpd/cases/${caseId}/images`, formData);
  return response.data;
};

const createOrUpdateNode = async ({ node }) => {
  const pathname = node?.uid ? `/cpd/idl/nodes/${node?.uid}` : `/cpd/idl/nodes`;
  if (node?.uid) {
    const response = await apiClient.put(`${pathname}`, {
      ...node,
    });
    return response.data;
  } else {
    const response = await apiClient.post(`${pathname}`, {
      ...node,
    });
    return response.data;
  }
};

const deleteCaseById = async (caseId) => {
  await apiClient.delete(`/cpd/cases/${caseId}`);
};

const deleteQuizQuestionById = async (url) => {
  const response = await apiClient.delete(url);
  return response.data;
};

const deleteNodeById = async (nodeId) => {
  const response = await apiClient.delete(`/cpd/idl/nodes/${nodeId}`);
  return response.data;
};

const deleteParagraphForNodeById = async (nodeId, paragraphId) => {
  const response = await apiClient.delete(
    `/cpd/idl/nodes/${nodeId}/paragraphs/${paragraphId}`
  );
  return response.data;
};

const deleteTableNodeById = async (nodeId, tableId) => {
  const response = await apiClient.delete(
    `/cpd/idl/nodes/${nodeId}/tables/${tableId}`
  );
  return response.data;
};

const deleteTableRowForTableNodeById = async (nodeId, tableId, tableRowId) => {
  const response = await apiClient.delete(
    `/cpd/idl/nodes/${nodeId}/tables/${tableId}/rows/${tableRowId}`
  );
  return response.data;
};

export {
  fetchCbdContentByType,
  fetchCbdContentNodeById,
  fetchOnlyCpdContentByQueryParams,
  fetchCbdQuizContentByNodeId,
  fetchCbdCases,
  fetchCbdCasesById,
  deleteCaseById,
  deleteQuizQuestionById,
  deleteNodeById,
  deleteParagraphForNodeById,
  deleteTableNodeById,
  deleteTableRowForTableNodeById,
  fetchCbdQuizForCaseById,
  fetchCpdContentNodesByQueryParams,
  fetchCbdContentSubNodesForNodeById,
  fetchCbdContentTablesForNodeById,
  fetchCbdContentTableRowsByNodeIdAndTableId,
  createOrUpdateCase,
  createOrUpdateCaseImage,
  createOrUpdateNode,
  sendCbdContentTableForNode,
  createOrUpdateCbdQuizForById,
  // CPD Analytics
  fetchCpdAnalyticsSummary,
  fetchCpdAnalyticsByDimension,
  fetchCpdUserProgress,
  fetchCpdTopPerformers,
};
